import { Box, Flex, TabPanel, TabPanels, Tabs, Text } from '@chakra-ui/react'
import { Icons } from '@ds/Icons'
import { ddsTokens } from '@ds/index'
import { DDSTheme } from '@ds/index'
import { useState } from 'react'
import { useTitle } from 'react-use'

import { operatorLabels } from '@/common/components/Table2/types'
import { useGetAllFormsQuery } from '@/modules/forms/api'

import {
	useGetMeetingBookedDataQuery,
	useGetMeetingsForTeamQuery,
} from '../api'
import AnalyticsTab from '../components/AnalyticsTab'
import { DateRangePicker } from '../components/DateRangePicker'
import { DisplayTag } from '../components/DisplayTag'
import { FormAnalytics } from '../components/FormAnalytics'
import FormFilter from '../components/FormFilter'
import { GranularitySelect } from '../components/GranularitySelect'
import { MeetingAnalytics } from '../components/MeetingAnalytics'
import {
	FilterState,
	Granularity,
	ReportingFilterFieldEnum,
	TimeFrame,
} from '../types'
import { FILTER_LABELS } from '../utils'

export const ReportingPage = () => {
	useTitle('Default - Dashboards')
	const [datePreset, setDatePreset] = useState<TimeFrame>(
		TimeFrame.LAST_30_DAYS,
	)
	const [granularity, setGranularity] = useState<Granularity>(
		Granularity.WEEKLY,
	)
	const [tabIndex, setTabIndex] = useState(0)
	const tokens = ddsTokens[DDSTheme.light]
	const [filters, setFilters] = useState<FilterState>({})
	const { data: formsFromApi } = useGetAllFormsQuery()

	// Meeting Booked Rate + Form Analytics
	const { data: meetingsBookedData, isLoading: isMeetingsBookedLoading } =
		useGetMeetingBookedDataQuery({
			datePreset,
			additionalFilters: filters,
		})

	// Meetings Scheduled + Average Time to Meet
	const { data: allMeetingsData, isLoading: isAllMeetingsLoading } =
		useGetMeetingsForTeamQuery({
			datePreset,
			granularity,
		})

	const handleDatePresetChange = (preset: TimeFrame) => {
		setDatePreset(preset)
	}

	const handleGranularityChange = (newGranularity: Granularity) => {
		setGranularity(newGranularity)
	}

	const handleFormFiltersChange = (filters: FilterState) => {
		setFilters(filters)
	}

	const removeFilter = (fieldToRemove: ReportingFilterFieldEnum) => {
		const updatedFilters = { ...filters }
		delete updatedFilters[fieldToRemove]
		setFilters(updatedFilters)
	}

	const clearAllFilters = () => {
		setFilters({})
	}

	const handleTabChange = (index: number) => {
		setTabIndex(index)
	}

	// Helper to get form name by ID
	const getFormNameById = (formId: string) => {
		if (!formsFromApi) return formId
		const form = formsFromApi.find((f: any) => f.id === formId)
		return form ? form.name : formId
	}

	const formatFilterValue = (
		field: ReportingFilterFieldEnum,
		value: any,
	): string => {
		if (field === ReportingFilterFieldEnum.FORM_ID && Array.isArray(value)) {
			if (value.length === 1) {
				return getFormNameById(value[0]) || value[0]
			}

			// Display all form names when multiple forms are selected
			const formNames = value.map((formId) => getFormNameById(formId))
			return formNames.join(', ')
		}
		return String(value)
	}

	return (
		<Box pt={4} w="full" bg="white" overflow="scroll">
			<Flex direction="column" gap={6}>
				<Flex direction="column" gap={2}>
					<Flex
						alignItems="center"
						alignContent="center"
						gap={2}
						px={6}
						pb={2}
						borderBottom={`1px solid ${tokens.primitive.light.gray[100]}`}
					>
						<Flex>
							<Text fontSize="xl" fontWeight="semibold">
								Dashboards
							</Text>
						</Flex>
					</Flex>

					<Tabs
						variant="unstyled"
						index={tabIndex}
						onChange={handleTabChange}
						display="flex"
						flexDirection="column"
						overflow="hidden"
						flex="1"
					>
						<Flex
							mb={4}
							pl={2}
							gap={2}
							pt={1}
							pb={3}
							borderBottom="1px solid"
							borderColor={tokens.primitive.light.gray[100]}
						>
							<AnalyticsTab
								label="Meetings"
								isSelected={tabIndex === 0}
								onClick={() => handleTabChange(0)}
							/>
							<AnalyticsTab
								label="Forms"
								isSelected={tabIndex === 1}
								onClick={() => handleTabChange(1)}
							/>
							<AnalyticsTab label="Pipeline" isDisabled comingSoon />
							<AnalyticsTab label="Routing" isDisabled comingSoon />
						</Flex>

						<Flex gap={4} align="center" justify="space-between" mt={2} px={6}>
							<Flex direction="column">
								{tabIndex !== 0 && (
									<>
										<FormFilter
											onFiltersChange={handleFormFiltersChange}
											existingFilters={filters}
										/>
										{Object.keys(filters).length > 0 && (
											<Flex mt={2} wrap="wrap" gap={2}>
												{Object.entries(filters).map(([field, filterOp]) => {
													const fieldEnum = field as ReportingFilterFieldEnum
													return (
														<DisplayTag
															key={field}
															value={{
																label: FILTER_LABELS[fieldEnum],
																operator: operatorLabels[filterOp.operator],
																value: formatFilterValue(
																	fieldEnum,
																	filterOp.value,
																),
																color: tokens.primitive.light.primary[500],
																icon: Icons.filter_funnel,
															}}
															onRemove={() => removeFilter(fieldEnum)}
														/>
													)
												})}
												{Object.keys(filters).length > 1 && (
													<DisplayTag
														value={{
															label: 'Clear All',
															color: tokens.primitive.light.gray[600],
															icon: Icons.refresh,
														}}
														onRemove={clearAllFilters}
													/>
												)}
											</Flex>
										)}
									</>
								)}
							</Flex>
							<Flex gap={2} align="center">
								<DateRangePicker
									preset={datePreset}
									onPresetChange={handleDatePresetChange}
								/>
								<GranularitySelect
									value={granularity}
									onChange={handleGranularityChange}
									datePreset={datePreset}
								/>
							</Flex>
						</Flex>

						<TabPanels overflow="auto" flex="1">
							<TabPanel p={6} pt={4}>
								<MeetingAnalytics
									allMeetings={allMeetingsData}
									granularity={granularity}
									datePreset={datePreset}
									isLoadingAllMeetings={isAllMeetingsLoading}
								/>
							</TabPanel>
							<TabPanel p={0} pt={4}>
								<FormAnalytics
									meetingsBookedData={meetingsBookedData}
									granularity={granularity}
									timeFrame={datePreset}
									isLoading={isMeetingsBookedLoading}
								/>
							</TabPanel>
						</TabPanels>
					</Tabs>
				</Flex>
			</Flex>
		</Box>
	)
}
